import { FieldValue, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  PHONE_1,
  PHONE_2,
  ACCOUNT_NUMBER,
  BANK_NAME,
  CURRENCY,
  LEASE_DUE_DATE,
  MONTHLY_DUE_DATE,
  ROUTING_NUMBER,
  YEARLY_DUE_DATE,
  REPORTING_LEVEL,
  DISTRIBUTION_MODE,
  FREQUENCY_FOR_ACTUALS,
  ACTUALS_DAY_OF_MONTH,
  ACTUALS_DATE,
  ACTUALS_DATE_Q1,
  ACTUALS_DATE_Q2,
  ACTUALS_DATE_Q3,
  ACTUALS_DATE_Q4,
  MONTHLY,
  QUARTERLY,
  YEARLY,
  actualsScheduleFields,
  quarterlyDateFields,
  NONE,
} from "./consts";
import {
  useGetCommunityFinancialInfo,
  useUpdateFinancialInfoMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { routes } from "@/routes/utils";
import {
  activeLevels,
  getRequiredFields,
  reportingLevelOptions,
} from "@/lib/utils";
import { financeSchema } from "./validations";

const useStep3 = ({ onNextClick }: { onNextClick?: () => void }) => {
  const { id, action } = useParams();
  const form = useForm<any>({
    defaultValues: {
      [YEARLY_DUE_DATE]: undefined,
      [MONTHLY_DUE_DATE]: undefined,
      [LEASE_DUE_DATE]: undefined,
      [CURRENCY]: "USD",
      [BANK_NAME]: undefined,
      [ROUTING_NUMBER]: undefined,
      [ACCOUNT_NUMBER]: undefined,
      [PHONE_1]: undefined,
      [PHONE_2]: undefined,
      [REPORTING_LEVEL]: "COMMUNITY",
      [DISTRIBUTION_MODE]: "PLAN_AND_ACTUAL",
      [FREQUENCY_FOR_ACTUALS]: undefined,
      [ACTUALS_DAY_OF_MONTH]: undefined,
      [ACTUALS_DATE]: undefined,
      [ACTUALS_DATE_Q1]: undefined,
      [ACTUALS_DATE_Q2]: undefined,
      [ACTUALS_DATE_Q3]: undefined,
      [ACTUALS_DATE_Q4]: undefined,
    },
    resolver: zodResolver(financeSchema),
  });
  const navigate = useNavigate();
  const isEditDetails = action === "edit-details";
  const isAdd = action == "add";
  const [monthlyDateKey, setMonthlyDateKey] = useState<number>(
    Math.random() * 2
  );
  const actualsFrequency = useWatch({control: form.control, name: FREQUENCY_FOR_ACTUALS});
  const distributionMode = useWatch({control: form.control, name: DISTRIBUTION_MODE});
  const reportingLevel = useWatch({control: form.control, name: REPORTING_LEVEL});

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(financeSchema);

  const {
    data: communityFinancialInfo,
    isLoading: isFinancialInfoLoading,
    isRefetching: isFinancialInfoRefetching,
  } = useGetCommunityFinancialInfo({
    community_id: id || "",
    queryParams: {
      enabled: !!(id && !isAdd),
    },
  });

  useEffect(() => {
    if (communityFinancialInfo) {
      form.reset({
        [YEARLY_DUE_DATE]:
          communityFinancialInfo?.data?.[YEARLY_DUE_DATE] || undefined,
        [MONTHLY_DUE_DATE]:
          communityFinancialInfo?.data?.[MONTHLY_DUE_DATE]?.toString() ||
          undefined,
        [LEASE_DUE_DATE]:
          communityFinancialInfo?.data?.[LEASE_DUE_DATE] || undefined,
        [CURRENCY]: communityFinancialInfo?.data?.[CURRENCY] || undefined,
        [BANK_NAME]: communityFinancialInfo?.data?.[BANK_NAME] || undefined,
        [ROUTING_NUMBER]:
          communityFinancialInfo?.data?.[ROUTING_NUMBER] || undefined,
        [ACCOUNT_NUMBER]:
          communityFinancialInfo?.data?.[ACCOUNT_NUMBER] || undefined,
        [PHONE_1]: communityFinancialInfo?.data?.[PHONE_1] || undefined,
        [PHONE_2]: communityFinancialInfo?.data?.[PHONE_2] || undefined,
        [REPORTING_LEVEL]:
          communityFinancialInfo?.data?.[REPORTING_LEVEL] || "COMMUNITY",
        [DISTRIBUTION_MODE]:
          communityFinancialInfo?.data?.[DISTRIBUTION_MODE] || "PLAN_AND_ACTUAL",
        [FREQUENCY_FOR_ACTUALS]:
          communityFinancialInfo?.data?.[FREQUENCY_FOR_ACTUALS] || undefined,
        [ACTUALS_DAY_OF_MONTH]:
          communityFinancialInfo?.data?.[ACTUALS_DAY_OF_MONTH]?.toString() ||
          undefined,
        [ACTUALS_DATE]:
          communityFinancialInfo?.data?.[ACTUALS_DATE] || undefined,
        [ACTUALS_DATE_Q1]:
          communityFinancialInfo?.data?.[ACTUALS_DATE_Q1] || undefined,
        [ACTUALS_DATE_Q2]:
          communityFinancialInfo?.data?.[ACTUALS_DATE_Q2] || undefined,
        [ACTUALS_DATE_Q3]:
          communityFinancialInfo?.data?.[ACTUALS_DATE_Q3] || undefined,
        [ACTUALS_DATE_Q4]:
          communityFinancialInfo?.data?.[ACTUALS_DATE_Q4] || undefined,
      });
      setMonthlyDateKey(Math.random() * 2);
    }
  }, [communityFinancialInfo]);

   const clearActualsScheduleFields = () => {
    actualsScheduleFields.forEach((field) => {
      form.setValue(field, undefined);
      form.clearErrors(field);
    });
  };

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateFinancialInfoMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        if (isEditDetails) {
          navigate(
            `/${routes.admin}/${routes.adminConsole}/${routes.communities}/${id}/${routes.details}`
          );
        } else {
          onNextClick && onNextClick();
        }
        toast.success("Info Updated Successfully");
      },
    });

  const onSubmit = (data: FieldValue<any>) => {
    let _data: any = {};
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
        _data[key] = data[key];
      }
    });

    const removeActualsScheduleFields = (fields: string[]) => {
      fields.forEach((field) => delete _data[field]);
    };

    if (_data[DISTRIBUTION_MODE] === "PLAN_ONLY") {
      delete _data[FREQUENCY_FOR_ACTUALS];
      removeActualsScheduleFields(actualsScheduleFields);
    } else if (_data[DISTRIBUTION_MODE] === "PLAN_AND_ACTUAL") {
      if (!_data[FREQUENCY_FOR_ACTUALS]) {
        form.setError(FREQUENCY_FOR_ACTUALS, {
          message: "Frequency For Actuals is Required",
        });
        return;
      }
    }

    if (_data[FREQUENCY_FOR_ACTUALS] === MONTHLY) {
      removeActualsScheduleFields([ACTUALS_DATE, ...quarterlyDateFields]);
    } else if (_data[FREQUENCY_FOR_ACTUALS] === QUARTERLY) {
      removeActualsScheduleFields([ACTUALS_DAY_OF_MONTH, ACTUALS_DATE]);
    } else if (_data[FREQUENCY_FOR_ACTUALS] === YEARLY) {
      removeActualsScheduleFields([
        ACTUALS_DAY_OF_MONTH,
        ...quarterlyDateFields,
      ]);
    } else {
      removeActualsScheduleFields(actualsScheduleFields);
    }

    if (
      _data[ROUTING_NUMBER] &&
      _data[ROUTING_NUMBER]?.toString().length !== 9
    ) {
      form.setError(ROUTING_NUMBER, {
        message: "Routing Number must be 9 digits",
      });
      return;
    }

    if (_data[ACCOUNT_NUMBER] && _data[ACCOUNT_NUMBER]?.toString().length < 6) {
      form.setError(ACCOUNT_NUMBER, {
        message: "Account Number must be more than 6 digits",
      });
      return;
    }

    updateInfo({ id: id || "", data: _data });
  };

  const activeThresholdIndex = activeLevels.findIndex(
    (level) => level.value == reportingLevel
  );
  
  const currentReportingLevelLabel = reportingLevelOptions.find((data: any) => data.value == reportingLevel)?.label;

  const onChangeAllocationMode = (value: any) => {
    if (value == NONE) {
      form.setValue(FREQUENCY_FOR_ACTUALS, "");
      form.setValue(ACTUALS_DAY_OF_MONTH, "");
      form.setValue(ACTUALS_DATE_Q1, undefined);
      form.setValue(ACTUALS_DATE_Q2, undefined);
      form.setValue(ACTUALS_DATE_Q3, undefined);
      form.setValue(ACTUALS_DATE_Q4, undefined);
      form.setValue(ACTUALS_DATE, undefined);
    }
  }

  return {
    form,
    onSubmit,
    requiredFields,
    isUpdatePending,
    isEditDetails,
    id,
    isFinancialInfoLoading,
    isFinancialInfoRefetching,
    monthlyDateKey,
    actualsFrequency,
    distributionMode,
    clearActualsScheduleFields,
    activeThresholdIndex,
    reportingLevel,
    currentReportingLevelLabel,
    onChangeAllocationMode
  };
};

export default useStep3;
